/**
 * PlanningConstantsPanel — Settings ▸ Planning. Resource Plan Epic 14 (RP-14.1):
 * "planning constants in Configuration, so that nothing is hard-coded".
 *
 * Each constant edits as a local draft with an explicit Save, following the
 * HoursPerIntensityPointBenchmark pattern in IntensityLevelsManager — a mutation per
 * keystroke on a firm-wide constant would be both noisy and audit-log spam.
 *
 * RP-14.1: "Changing a constant affects new computations only. Frozen versions retain the
 * values they were built on." The copy says so, because it is the question an admin will
 * ask before touching the margin floor.
 */

import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type PlanningConstant,
  usePlanningConstants,
  useSetPlanningConstant,
} from "@/hooks/use-rate-card";

export function PlanningConstantsPanel() {
  const { data: constants = [], isLoading, isError, error, refetch } = usePlanningConstants();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the planning constants"}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Planning Constants</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Firm-wide values every Resource Plan is built on. Changing one affects new computations
            only — a frozen plan version keeps the values it was built on.
          </p>
        </div>
      </header>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {constants.map((constant) => (
            <ConstantCard key={constant.key} constant={constant} />
          ))}
        </div>
      )}

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4" />
            Two constants that live elsewhere
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Hours per point</span> is the same figure
            as the hours-per-Intensity-point benchmark on the Intensity tab. It is kept as one value
            rather than two that must agree.
          </p>
          <p>
            <span className="font-medium text-foreground">
              Margin floor overrides per Rate Basis
            </span>{" "}
            sit on the Rate Card, next to the rates they qualify. The floor set here is the
            firm-wide figure a Rate Basis inherits when it carries no override.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ConstantCard({ constant }: { constant: PlanningConstant }) {
  const setConstant = useSetPlanningConstant();
  const [draft, setDraft] = useState(constant.value);

  // Re-sync when the server value changes underneath (another admin, or a refetch).
  useEffect(() => setDraft(constant.value), [constant.value]);

  const dirty = draft !== constant.value;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span>{constant.label}</span>
          {constant.isDefault && (
            <Badge variant="outline" className="text-xs font-normal">
              default
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{constant.description}</p>
        <div className="flex items-center gap-2">
          <Input
            aria-label={constant.label}
            // A string, not type="number": these are exact decimals, and a number input
            // would hand back a float64.
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-9 w-32 tabular-nums"
          />
          <Button
            size="sm"
            variant={dirty ? "default" : "outline"}
            disabled={!dirty || setConstant.isPending}
            onClick={() => setConstant.mutate({ name: constant.name, value: draft })}
          >
            {setConstant.isPending ? "Saving…" : "Save"}
          </Button>
          {dirty && (
            <Button size="sm" variant="ghost" onClick={() => setDraft(constant.value)}>
              Reset
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
